<template>
  <div
    class="hero-banner"
    :class="{ 'is-carousel': isCarousel }"
    @mouseenter="pauseCarousel"
    @mouseleave="resumeCarousel"
  >
    <div
      v-if="isCarousel"
      class="banner-carousel"
      @pointerdown="handlePointerDown"
      @pointermove="handlePointerMove"
      @pointerup="handlePointerUp"
      @pointercancel="handlePointerCancel"
    >
      <div
        class="banner-track"
        :class="{ dragging: isDragging }"
        :style="trackStyle"
      >
        <div
          v-for="(slide, index) in carouselSlides"
          :key="slide"
          class="banner-slide"
          :aria-hidden="index !== activeIndex"
        >
          <img
            :src="slide"
            :alt="$t('forms.lotteryBanner')"
            draggable="false"
          >
        </div>
      </div>

      <button
        class="carousel-control previous"
        type="button"
        :aria-label="$t('common.previous')"
        @pointerdown.stop
        @click.stop.prevent="previousSlide"
      >
        ‹
      </button>
      <button
        class="carousel-control next"
        type="button"
        :aria-label="$t('common.next')"
        @pointerdown.stop
        @click.stop.prevent="nextSlide"
      >
        ›
      </button>

      <div
        class="carousel-dots"
        role="tablist"
        @pointerdown.stop
      >
        <button
          v-for="(_, index) in carouselSlides"
          :key="index"
          class="carousel-dot"
          :class="{ active: index === activeIndex }"
          type="button"
          role="tab"
          :aria-selected="index === activeIndex"
          :aria-label="`${$t('common.view')} ${$t('forms.lotteryBanner')} ${index + 1}`"
          @click.stop.prevent="goToSlide(index)"
        />
      </div>
    </div>

    <picture v-else>
      <source media="(min-width: 768px)" :srcset="heroBanner" />
      <img
        :src="heroBannerMobile"
        :alt="$t('forms.lotteryBanner')"
      >
    </picture>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useTheme } from '@/composables/useTheme'
import { useGameStore } from '@/stores/game'

const { currentThemeName, getAssetPath } = useTheme()
const gameStore = useGameStore()

const uploadedBanner = (assetType: 'large' | 'small'): string => {
  const assets = gameStore.getBetSettings?.theme_assets
  const locale = new URLSearchParams(window.location.search).get('locale') || 'en'
  const localized = assets?.banners?.[locale]?.[assetType]?.url
  const englishFallback = assets?.banners?.en?.[assetType]?.url
  return localized || englishFallback || ''
}

const heroBanner = computed(() => uploadedBanner('large') || getAssetPath('heroBanner') || '/img/images/banners/banner-lg.png')
const heroBannerMobile = computed(() => uploadedBanner('small') || uploadedBanner('large') || getAssetPath('heroBannerMobile') || '/img/images/banners/banner-sm.png')

const activeIndex = ref(0)
const isDragging = ref(false)
const dragStartX = ref(0)
const dragDeltaX = ref(0)
let autoPlayTimer: number | undefined

const carouselSlides = computed(() => {
  if (currentThemeName.value !== 'wsb') return []
  return [heroBanner.value, heroBannerMobile.value].filter(Boolean)
})

const isCarousel = computed(() => carouselSlides.value.length > 1)

const trackStyle = computed(() => ({
  transform: `translateX(calc(-${activeIndex.value * 100}% + ${dragDeltaX.value}px))`
}))

const goToSlide = (index: number, restartAutoPlay = true) => {
  const total = carouselSlides.value.length
  if (!total) return
  activeIndex.value = (index + total) % total
  if (restartAutoPlay) resumeCarousel()
}

const nextSlide = () => goToSlide(activeIndex.value + 1)
const previousSlide = () => goToSlide(activeIndex.value - 1)

const pauseCarousel = () => {
  if (autoPlayTimer) {
    window.clearInterval(autoPlayTimer)
    autoPlayTimer = undefined
  }
}

const resumeCarousel = () => {
  pauseCarousel()
  if (isCarousel.value) {
    autoPlayTimer = window.setInterval(nextSlide, 6000)
  }
}

const handlePointerDown = (event: PointerEvent) => {
  if (!isCarousel.value) return
  pauseCarousel()
  isDragging.value = true
  dragStartX.value = event.clientX
  dragDeltaX.value = 0
  ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
}

const handlePointerMove = (event: PointerEvent) => {
  if (!isDragging.value) return
  dragDeltaX.value = event.clientX - dragStartX.value
}

const completeDrag = () => {
  if (!isDragging.value) return
  const threshold = 48
  if (dragDeltaX.value > threshold) {
    goToSlide(activeIndex.value - 1, false)
  } else if (dragDeltaX.value < -threshold) {
    goToSlide(activeIndex.value + 1, false)
  }
  isDragging.value = false
  dragDeltaX.value = 0
  resumeCarousel()
}

const handlePointerUp = () => completeDrag()
const handlePointerCancel = () => {
  isDragging.value = false
  dragDeltaX.value = 0
  resumeCarousel()
}

watch(carouselSlides, (slides) => {
  if (activeIndex.value >= slides.length) activeIndex.value = 0
  if (slides.length > 1) resumeCarousel()
}, { immediate: true })

onMounted(resumeCarousel)
onUnmounted(pauseCarousel)
</script>

<style scoped>
.hero-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
}

picture {
  width: 100%;
  height: auto;
  display: block;
}

.banner-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  touch-action: pan-y;
  user-select: none;
}

.banner-track {
  display: flex;
  width: 100%;
  transition: transform 0.35s ease;
  will-change: transform;
}

.banner-track.dragging {
  transition: none;
}

.banner-slide {
  flex: 0 0 100%;
  min-width: 100%;
}

img {
  width: 100%;
  height: auto;
  max-width: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  -webkit-user-drag: none;
}

.carousel-control {
  position: absolute;
  top: 50%;
  z-index: 2;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.42);
  color: #fff;
  cursor: pointer;
  font-size: 2rem;
  line-height: 1;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
}

.carousel-control.previous {
  left: 14px;
}

.carousel-control.next {
  right: 14px;
}

.carousel-dots {
  position: absolute;
  left: 50%;
  bottom: 12px;
  z-index: 2;
  display: flex;
  gap: 8px;
  transform: translateX(-50%);
}

.carousel-dot {
  width: 9px;
  height: 9px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
}

.carousel-dot.active {
  width: 24px;
  background: #fff;
}

@media (max-width: 767px) {
  .carousel-control {
    width: 32px;
    height: 32px;
    font-size: 1.65rem;
  }

  .carousel-control.previous {
    left: 8px;
  }

  .carousel-control.next {
    right: 8px;
  }

  .carousel-dots {
    bottom: 8px;
  }
}

</style> 
